// src/components/Settings/Layout/ProfileSettings.js

import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate }          from "react-router-dom";
import { ThemeContext }         from "../../../../../context/ThemeContext";
import { MediaDisplayContext }  from "../../../../../context/MediaDisplayModeContext";
import { FetchUserDataInSettings } from "../Utils/SettingsUserFetchMockData";
import ThemeSelector            from "../Utils/themeSelector";
import OTPHandler               from "../../../../common/OTPHandler/otphandler";
import { USE_MOCK_DATA }        from "../../../../config/apiConfig";
import { toast }                from "react-toastify";
import {
  Slideshow,
  GridLayout,
  HorizontalScroll,
  VerticalScroll,
  LightBox,
} from "../Utils/MediaViews";
import { color } from "framer-motion";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const PROFILE_PAGE_OPTIONS = [
  "music",
  "dance",
  "food",
  "hiking",
  "sports",
  "skydiving",
  "biking",
  "decor",
  "drama",
  "speaker",
  "fashion",
  "travel",
];

const FREE_PAGE_LIMIT = 5;
const MAX_PAGE_LIMIT = 8;
const EXTRA_PAGE_PRICE = 3;

const THEME_OPTIONS = [
  { value: "shinyBlack",     label: "Shiny Black"      },
  { value: "desertTitanium", label: "Desert Titanium"  },
  { value: "milkyWhite",     label: "Milky White"      },
  { value: "royalVelvet",    label: "Royal Velvet"     },
  { value: "dreamyLilac",    label: "Dreamy Lilac"     },
  { value: "sunsetRush",     label: "Sunset Rush"      },
  { value: "amberGlow",      label: "Amber Glow"       },
  { value: "blushPetal",     label: "Blush Petal"      },
  { value: "neonRose",       label: "Neon Rose"        },
  { value: "iceMint",        label: "Ice Mint"         },
  { value: "urbanSlate",     label: "Urban Slate"      },
  { value: "skylineBlue",    label: "Skyline Blue"     },
];

const MEDIA_MODE_OPTIONS = [
  { value: "slideshow",         label: "Slideshow / Carousel"    },
  { value: "grid",              label: "Grid Layout"             },
  { value: "horizontal-scroll", label: "Horizontal Scroll"       },
  { value: "vertical-scroll",   label: "Vertical Scroll"         },
  { value: "lightbox",          label: "Lightbox Viewing"        },
];

// ─────────────────────────────────────────────────────────────────────────────
// SHARED PRIMITIVE COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

/** Styled section divider with label */
const SubSection = ({ icon, title, children }) => (
  <div className="ps-subsection">
    <div className="ps-subsection-header">
      <span className="ps-subsection-icon">{icon}</span>
      <h3 className="ps-subsection-title">{title}</h3>
    </div>
    <div className="ps-subsection-body">{children}</div>
  </div>
);

/** Consistent form row: label + control stacked */
const FormRow = ({ label, hint, children, htmlFor }) => (
  <div className="ps-form-row" style={{marginTop: 20}}>
    <label className="ps-form-label" htmlFor={htmlFor}>
      {label}
      {label === "New Password" && hint ? ` (${hint})` : ""}
    </label>
    {children}
  </div>
);

/** Styled select */
const PSSelect = ({ id, value, onChange, options, style }) => (
  <select id={id} value={value} onChange={onChange} className="ps-select" style={style}>
    {options.map(o => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

/** Toggle checkbox row */
const ToggleRow = ({ label, desc, checked, onChange }) => (
  <div className="ps-toggle-row" style={{marginTop: 30, display: "flex", flexDirection: "row" }}>
    <label className="st-switch">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="st-slider" />
    </label>

    <div className="ps-toggle-info" style={{marginLeft: 20}}>
      <span className="ps-toggle-label">{label}</span>
    </div>
  </div>
);

const FlashBanner = ({ message, type = "success" }) =>
  message ? (
    <div
      className={`ps-flash ps-flash--${type}`}
      style={{ color: "white", marginTop: 20 }}
    >
      {message}
    </div>
  ) : null;

/** Per-profile expandable row */
const ProfileRow = ({ profile, changes, onThemeChange, onMediaChange }) => {
  const [open, setOpen] = useState(false);
  const uuid = profile.profileUUID;

  return (
    <div className="ps-profile-row">
      <button
        type="button"
        className="ps-profile-row-header"
        onClick={() => setOpen(p => !p)}
      >
        <span className="ps-profile-row-avatar">👤</span>
        <span className="ps-profile-row-name">{profile.profilename}</span>
        <span className={`ps-profile-row-chevron ${open ? "ps-profile-row-chevron--open" : ""}`}>›</span>
      </button>

      {open && (
        <div className="ps-profile-row-body">
          {/* Theme */}
          <div className="ps-profile-row-field">
            <span className="ps-form-label">Theme</span>
            <span className="ps-current-tag">Current: {profile.theme || "default"}</span>
            <PSSelect
              value={changes.theme ?? profile.theme ?? "shinyBlack"}
              onChange={e => onThemeChange(uuid, e.target.value)}
              options={THEME_OPTIONS}
            />
          </div>
          {/* Media */}
          <div className="ps-profile-row-field">
            <span className="ps-form-label">Media Display</span>
            <span className="ps-current-tag">Current: {profile.mediaDisplayMode || "slideshow"}</span>
            <PSSelect
              value={changes.mediaDisplayMode ?? profile.mediaDisplayMode ?? "slideshow"}
              onChange={e => onMediaChange(uuid, "mediaDisplayMode", e.target.value)}
              options={MEDIA_MODE_OPTIONS}
              background="rgba(36,36,45,0.2)"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// INNER SECTION: FEED VIEW SETTINGS
// ─────────────────────────────────────────────────────────────────────────────

// ─── Module-level option constants (fix #6) ───────────────────────────────────
const MULTIPAGE_MODE_OPTIONS = [
  { value: "disable", label: "Disable Multipage" },
  { value: "enable", label: "Enable Multipage" },
];


const FeedViewSection = () => {
  const [flash, setFlash] = useState("");
  const [draggedProfile, setDraggedProfile] = useState(null);

  const [settings, setSettings] = useState({
    feedViewMode: "singlepage",
    multipageMode: false,
    pageCount: 1,
    selectedProfiles: [],
    extraPageCost: 0,
  });

  useEffect(() => {
    try {
      const loggedIn = JSON.parse(localStorage.getItem("userLoggedin") || "{}");
      const users = JSON.parse(localStorage.getItem("users") || "[]");
      const current = users.find((u) => u.userId === loggedIn.userId);

      if (current) {
        const pageCount = Number(current.pageCount || 1);
        setSettings({
          feedViewMode: current.feedViewMode || "singlepage",
          multipageMode: !!current.multipageMode,
          pageCount,
          selectedProfiles: current.selectedProfiles || [],
          extraPageCost: Math.max(0, pageCount - FREE_PAGE_LIMIT) * EXTRA_PAGE_PRICE,
        });
      }
    } catch (_) {}
  }, []);

  const availableProfiles = PROFILE_PAGE_OPTIONS.filter(
    (profile) => !settings.selectedProfiles.includes(profile)
  );

  const calculateExtraCost = (count) =>
    Math.max(0, count - FREE_PAGE_LIMIT) * EXTRA_PAGE_PRICE;

  const updatePageCount = (value) => {
    const count = Math.min(
      MAX_PAGE_LIMIT,
      Math.max(1, Number(value) || 1)
    );

    setSettings((prev) => ({
      ...prev,
      pageCount: count,
      selectedProfiles: prev.selectedProfiles.slice(0, count),
      extraPageCost: calculateExtraCost(count),
    }));
  };

  const handleFeedModeChange = (mode) => {
    if (mode === "multipage") {
      const requestedPages = window.prompt(
        "How many pages/profiles do you want to create? Each page is one profile. 5 pages are free. Extra pages cost $3 per page. Maximum 8 pages supported.",
        String(settings.pageCount || FREE_PAGE_LIMIT)
      );

      // Cancelled prompt → keep current (single page) mode instead of defaulting.
      if (requestedPages === null) return;

      const pageCount = Math.min(
        MAX_PAGE_LIMIT,
        Math.max(1, Number(requestedPages) || FREE_PAGE_LIMIT)
      );

      setSettings((prev) => ({
        ...prev,
        feedViewMode: "multipage",
        multipageMode: true,
        pageCount,
        selectedProfiles: prev.selectedProfiles.slice(0, pageCount),
        extraPageCost: calculateExtraCost(pageCount),
      }));

      return;
    }

    setSettings((prev) => ({
      ...prev,
      feedViewMode: "singlepage",
      multipageMode: true,
      pageCount: 1,
      selectedProfiles: [],
      extraPageCost: 0,
    }));
  };

    // Fix #5: handleMultipageModeChange
    function handleMultipageModeChange(e) {
      const val = e.target.value;
      if (val === "enable") {
        setSettings((prev) => ({
          ...prev,
          multipageMode: true,
        }));
      } else {
        // Fix #2 (was multipageFeedMode) + fix #5: reset all related state
        setSettings((prev) => ({
          ...prev,
          feedViewMode: "singlepage",
          multipageMode: false,   // fix #2: was multipageFeedMode
        }));
      }
    }
  

  const addProfile = (profile) => {
    setSettings((prev) => {
      if (prev.selectedProfiles.includes(profile)) return prev;

      if (prev.selectedProfiles.length >= prev.pageCount) {
        setFlash(`You can select only ${prev.pageCount} profile/page option(s).`);
        setTimeout(() => setFlash(""), 3000);
        return prev;
      }

      return {
        ...prev,
        selectedProfiles: [...prev.selectedProfiles, profile],
      };
    });
  };

  const removeProfile = (profile) => {
    setSettings((prev) => ({
      ...prev,
      selectedProfiles: prev.selectedProfiles.filter((p) => p !== profile),
    }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (draggedProfile) addProfile(draggedProfile);
    setDraggedProfile(null);
  };

  const handleSave = (e) => {
    e.preventDefault();

    try {
      const loggedIn = JSON.parse(localStorage.getItem("userLoggedin") || "{}");
      const users = JSON.parse(localStorage.getItem("users") || "[]");
      const idx = users.findIndex((u) => u.userId === loggedIn.userId);

      if (idx === -1) {
        setFlash("User not found.");
        return;
      }

      users[idx] = { ...users[idx], ...settings };
      localStorage.setItem("users", JSON.stringify(users));

      setFlash("✅ Feed settings saved!");
      setTimeout(() => setFlash(""), 3000);
    } catch (_) {
      setFlash("Failed to save settings.");
      setTimeout(() => setFlash(""), 3000);
    }
  };

  return (
    <SubSection>
      <form
        onSubmit={handleSave}
        className="ps-form"
        style={{ marginTop: 30, color: "white" }}
      >
        <div className="ps-form-grid-2" style={{background: "rgba(36,36,45,0.2)", width: "20%" }}>
        <FormRow label="Multipage Mode">
          <PSSelect
            value={settings.multipageMode ? "enable" : "disable"}
            onChange={handleMultipageModeChange}
            options={MULTIPAGE_MODE_OPTIONS}
            style={{background: "rgba(36,36,45,0.2)", border: "2px solid darkslategray", height: 50}}
          />
        </FormRow>
        </div>

        {settings.multipageMode && (
          <div style={{ marginTop: 20 }}>
            <div
              className="ps-flash ps-flash--success"
              style={{ color: "white", marginTop: 20, width: "80%", height: 100, border: "1px solid black" }}
            >
              <span style={{display: "flex", alignContent: "center", alignSelf: "center", marginTop: 20}}>
              Note: Multi Page enables “Design Your Own Page”. Each page is one profile.
              Feed can be filtered per page, with more admirer options for larger connections.
              </span>
            </div>

            <FormRow label="Number of Pages / Profiles: " htmlFor="pageCount" color="white">
              <input
                id="pageCount"
                type="number"
                min="1"
                max={MAX_PAGE_LIMIT}
                value={settings.pageCount}
                onChange={(e) => updatePageCount(e.target.value)}
                className="ps-input"
                style={{ marginTop: 20, marginLeft: 30, background: "black", color: "white", borderRadius: 5, border: "2px solid darkslategray", height: 30 }}
              />
            <div
              className="ps-flash ps-flash--success"
              style={{ color: "white", marginTop: 20, width: "80%", height: 100, border: "1px solid black" }}
            >
              <p style={{ marginTop: 10, color: "white" }}>
                5 pages are free. Extra pages cost ${EXTRA_PAGE_PRICE} per page.
                Maximum {MAX_PAGE_LIMIT} pages supported.
              </p>
              <p style={{ marginTop: 10, color: "white" }}>
                Estimated extra cost: ${settings.extraPageCost}
              </p>
              </div>
            </FormRow>

            <div
              className="ps-form-grid-2"
              style={{ marginTop: 30, gap: 20, display: "flex", flexDirection: "row" }}
            >
              <div className="ps-profile-list" style={{ minHeight: 240, width: "40%", background: "black", border: "1px solid gray" }}>
                <div style={{background: "white", height: 50, alignContent: "center", padding: 10, border: "3px solid darkslategray" }}>
                <h4 style={{ color: "black", marginBottom: 15, marginLeft: 10 }}>
                  Available Profiles
                </h4>
                </div>

                {availableProfiles.map((profile) => (
                  <div
                    key={profile}
                    draggable
                    onDragStart={() => setDraggedProfile(profile)}
                    onClick={() => addProfile(profile)}
                    className="ps-profile-row-header"
                    style={{
                      marginTop: 10,
                      cursor: "grab",
                      color: "white",
                      textTransform: "capitalize",
                      marginLeft: 10
                    }}
                  >
                    {profile}
                  </div>
                ))}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  width: "10%"
                }}
              >
                <span>--&gt;</span>
                <span>&lt;--</span>
              </div>
              <div
                className="ps-profile-list"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                style={{
                  minHeight: 240,
                  border: "1px dashed gray",
                  width: "40%",
                  background: "black", 
                  color: "gray",
                }}
              >
                <div style={{background: "white", height: 50, alignContent: "center", border: "3px solid darkslategray", marginTop: 0, padding: 10 }}>
                  <h4 style={{ color: "black", marginBottom: 15, marginLeft: 10 }}>
                    Selected Profiles ({settings.selectedProfiles.length}/
                    {settings.pageCount})
                  </h4>
                </div>

                {settings.selectedProfiles.length === 0 ? (
                  <p style={{ color: "white", marginTop: 20, marginLeft: 10 }}>
                    Drag profile options here.
                  </p>
                ) : (
                  settings.selectedProfiles.map((profile) => (
                    <div
                      key={profile}
                      className="ps-profile-row-header"
                      style={{
                        marginTop: 10,
                        color: "white",
                        textTransform: "capitalize",
                        marginLeft: 10
                      }}
                    >
                      <span>{profile}</span>
                      <button
                        type="button"
                        onClick={() => removeProfile(profile)}
                        style={{
                          marginLeft: 15,
                          color: "white",
                          background: "transparent",
                          border: "1px solid gray",
                          borderRadius: 8,
                          padding: "4px 8px",
                          cursor: "pointer",
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div style={{display: "flex", flexDirection: "column" }}>
            <FormRow label="Feed View Mode" htmlFor="feedViewMode" style={{background: "rgba(36,36,45,0.2)"}}>
              <div>
            <PSSelect
              id="feedViewMode"
              value={settings.feedViewMode}
              onChange={(e) => handleFeedModeChange(e.target.value)}
              options={[
                { value: "singlepage", label: "Single Page" },
                { value: "multipage", label: "Multi Page" },
              ]}
              style={{background: "rgba(36,36,45,0.2)", width: "20%", height: 30, border: "2px solid darkslategray"}}
            />
            </div>
          </FormRow>
          </div>
          </div>
        )}

        <FlashBanner
          message={flash}
          type={flash.startsWith("✅") ? "success" : "warning"}
        />

        <button
          type="submit"
          className="st-save-btn ps-action-btn"
          style={{
            background: "rgba(113, 113, 147, 0.2)",
            marginTop: 30,
            color: "white",
          }}
        >
          Save Feed Settings
        </button>
      </form>
    </SubSection>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// INNER SECTION: THEME SETTINGS
// ─────────────────────────────────────────────────────────────────────────────

const ThemeSection = ({ profileData, updatedProfiles, onProfileChange }) => {
  const [explicitTheme,       setExplicitTheme]       = useState(
    () => JSON.parse(localStorage.getItem("explicitTheme") || "false")
  );
  const [perProfileTheme,     setPerProfileTheme]     = useState(
    () => JSON.parse(localStorage.getItem("perProfileThemeEnabled") || "false")
  );

  useEffect(() => {
    localStorage.setItem("explicitTheme",          JSON.stringify(explicitTheme));
    localStorage.setItem("perProfileThemeEnabled", JSON.stringify(perProfileTheme));
  }, [explicitTheme, perProfileTheme]);

  return (
    <SubSection>
      <div className="ps-form">
        <ToggleRow
          label="Enable Global Theme Override"
          checked={!!explicitTheme}
          onChange={() => setExplicitTheme(p => !p)}
        />
        {explicitTheme && (
          <div className="ps-indent">
            <ThemeSelector />
          </div>
        )}

        <div className="ps-divider" />

        <ToggleRow
          label="Enable Per-Profile Theme Customisation"
          checked={perProfileTheme}
          onChange={() => setPerProfileTheme(p => !p)}
        />

        {perProfileTheme && (
          <div className="ps-profile-list">
            {profileData.length === 0 ? (
              <p className="ps-empty-note">No profiles found. Create a profile first.</p>
            ) : (
              profileData.map(profile => (
                <ProfileRow
                  key={profile.profileUUID}
                  profile={profile}
                  changes={updatedProfiles[profile.profileUUID] || {}}
                  onThemeChange={(uuid, val) => onProfileChange(uuid, "theme", val)}
                  onMediaChange={onProfileChange}
                />
              ))
            )}
          </div>
        )}
      </div>
    </SubSection>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// INNER SECTION: MEDIA DISPLAY
// ─────────────────────────────────────────────────────────────────────────────

const MediaSection = ({ profileData, updatedProfiles, onProfileChange }) => {
  const { displayMode, setDisplayMode } = useContext(MediaDisplayContext);
  const [perProfileMedia, setPerProfileMedia] = useState(false);

  const PREVIEW_MAP = {
    "slideshow":         <Slideshow />,
    "grid":              <GridLayout />,
    "horizontal-scroll": <HorizontalScroll />,
    "vertical-scroll":   <VerticalScroll />,
    "lightbox":          <LightBox />,
  };

  return (
    <SubSection>
      <div className="ps-form">
        {/* Live preview */}
        <div className="ps-media-preview-wrap">
          <div className="ps-media-preview-box" style={{marginLeft: 10}}>
            {PREVIEW_MAP[displayMode] || null}
          </div>
        </div>

        <FormRow htmlFor="globalMediaMode">
          <PSSelect
            id="globalMediaMode"
            value={displayMode}
            onChange={e => setDisplayMode(e.target.value)}
            options={MEDIA_MODE_OPTIONS}
            style={{background: "rgba(36,36,45,0.2)", color: "white" }}
          />
        </FormRow>

        <div className="ps-divider" />

        <ToggleRow
          label="Set Media Display Per Profile"
          desc="Override the global setting for individual profiles."
          checked={perProfileMedia}
          onChange={() => setPerProfileMedia(p => !p)}
        />

        {perProfileMedia && (
          <div className="ps-profile-list">
            {profileData.length === 0 ? (
              <p className="ps-empty-note">No profiles found. Create a profile first.</p>
            ) : (
              profileData.map(profile => (
                <ProfileRow
                  key={profile.profileUUID}
                  profile={profile}
                  changes={updatedProfiles[profile.profileUUID] || {}}
                  onThemeChange={(uuid, val) => onProfileChange(uuid, "theme", val)}
                  onMediaChange={onProfileChange}
                />
              ))
            )}
          </div>
        )}
      </div>
    </SubSection>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// INNER SECTION: UPDATE PASSWORD
// ─────────────────────────────────────────────────────────────────────────────

const PasswordSection = () => {
  const [form, setForm]             = useState({ current: "", next: "", confirm: "" });
  const [message, setMessage]       = useState({ text: "", type: "success" });
  const [isOTPVerified, setOTPVerified] = useState(false);

  const setField = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleOTPVerified = (data) => {
    setOTPVerified(true);
    setMessage({ text: "✅ Mobile number verified. You may now update your password.", type: "success" });
    toast.success("OTP verified!");
    if (data?.deviceId) localStorage.setItem("deviceId", data.deviceId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { current, next, confirm } = form;

    if (!current || !next || !confirm) {
      setMessage({ text: "All fields are required.", type: "error" }); return;
    }
    if (next.length < 8) {
      setMessage({ text: "New password must be at least 8 characters.", type: "error" }); return;
    }
    if (next !== confirm) {
      setMessage({ text: "New passwords do not match.", type: "error" }); return;
    }
    if (!isOTPVerified) {
      setMessage({ text: "Please verify your mobile number with OTP first.", type: "error" }); return;
    }

    try {
      const res  = await fetch("/api/update-password", {
        method : "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("securityToken")}`,
        },
        body: JSON.stringify({ currentPassword: current, newPassword: next, deviceId: localStorage.getItem("deviceId") }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ text: "✅ Password updated successfully!", type: "success" });
        toast.success("Password updated!");
        setForm({ current: "", next: "", confirm: "" });
        setOTPVerified(false);
      } else {
        setMessage({ text: data.error || "Failed to update password.", type: "error" });
        toast.error(data.error || "Failed to update password.");
      }
    } catch {
      setMessage({ text: "An error occurred. Please try again.", type: "error" });
    }
  };

  return (
    <SubSection>
      {USE_MOCK_DATA && (
        <div className="ps-mock-banner">
          🎭 Mock Mode — any 6-digit OTP is accepted for testing
        </div>
      )}

      <form onSubmit={handleSubmit} className="ps-form" autoComplete="off">
        {/* Password fields */}
        <div className="ps-password-grid">
          <FormRow label="Current Password" htmlFor="pw-current">
            <input
              id="pw-current"
              type="password"
              className="ps-input"
              value={form.current}
              onChange={e => setField("current", e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />
          </FormRow>

          <FormRow label="New Password" htmlFor="pw-new"
            hint="Minimum 8 characters"
          >
            <input
              id="pw-new"
              type="password"
              className="ps-input"
              value={form.next}
              onChange={e => setField("next", e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              required
            />
          </FormRow>

          <FormRow label="Confirm Password" htmlFor="pw-confirm">
            <input
              id="pw-confirm"
              type="password"
              className={`ps-input ${
                form.confirm && form.next !== form.confirm ? "ps-input--error" : ""
              } ${
                form.confirm && form.next === form.confirm && form.confirm.length > 0
                  ? "ps-input--success" : ""
              }`}
              value={form.confirm}
              onChange={e => setField("confirm", e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              required
            />
            {form.confirm && form.next !== form.confirm && (
              <span className="ps-inline-error">Passwords do not match</span>
            )}
            {form.confirm && form.next === form.confirm && form.confirm.length > 0 && (
              <span className="ps-inline-success">✓ Passwords match</span>
            )}
          </FormRow>
        </div>

        {/* OTP */}
        <div className="ps-otp-section">
          <p className="ps-form-label" style={{ marginTop: 30 }}>
            Mobile OTP Verification*
          </p>
          <OTPHandler
            onOTPVerified={handleOTPVerified}
            initialMobile=""
            autoOpenModal={false}
            color="black"
          />
          {isOTPVerified && (
            <div className="ps-otp-verified" style={{color: "black"}}>
              ✅ Mobile verified — you may now submit
            </div>
          )}
        </div>

        {/* Status message */}
        {message.text && (
          <FlashBanner message={message.text} type={message.type} />
        )}

        <button
          type="submit"
          className="st-save-btn ps-action-btn"
          disabled={!isOTPVerified}
          style={{ opacity: isOTPVerified ? 1 : 0.45, cursor: isOTPVerified ? "pointer" : "not-allowed", background: "rgba(36,36,45,0.2)", border: "1px solid gray" }}
        >
          🔒 Update Password
        </button>
      </form>
    </SubSection>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT — ProfileSettings
// ─────────────────────────────────────────────────────────────────────────────

const INNER_TABS = [
  { key: "feed",     label: "📰 Feed View"  },
  { key: "themes",   label: "🎨 Themes"     },
  { key: "media",    label: "🖼️ Media"      },
  { key: "password", label: "🔑 Password"   },
];

const ProfileSettings = () => {
  const navigate                              = useNavigate();
  const { theme }                             = useContext(ThemeContext);
  const [innerTab,       setInnerTab]         = useState("feed");
  const [profileData,    setProfileData]      = useState([]);
  const [userid,         setUserid]           = useState("");
  const [updatedProfiles, setUpdatedProfiles] = useState({});
  const [submitFlash,    setSubmitFlash]      = useState("");

  // ── Load theme CSS ────────────────────────────────────────────────────
  useEffect(() => {
    import(`../Styles/themes/${theme}/themes.css`)
      .catch(() => {});
  }, [theme]);

  // ── Fetch profiles ────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const data = await FetchUserDataInSettings();
        if (data && Array.isArray(data.profiles)) {
          const withUUID = data.profiles.map((p, i) => ({
            profileUUID: p.profileUUID || `uuid-${p.profilename}-${i}`,
            ...p,
          }));
          setUserid(data.userId || "");
          setProfileData(withUUID);
        }
      } catch (err) {
        console.error("Failed to load profile data:", err);
      }
    };
    load();
  }, []);

  // ── Unified profile change handler ────────────────────────────────────
  const handleProfileChange = (uuid, key, value) => {
    setUpdatedProfiles(prev => ({
      ...prev,
      [uuid]: { ...prev[uuid], [key]: value },
    }));
  };

  // ── Submit all per-profile changes ────────────────────────────────────
  const handleSubmitAll = () => {
    const summary = profileData.map(p => {
      const ch    = updatedProfiles[p.profileUUID] || {};
      const theme = ch.theme            ?? p.theme            ?? "default";
      const media = ch.mediaDisplayMode ?? p.mediaDisplayMode ?? "default";
      return `• ${p.profilename}: Theme = "${theme}", Media = "${media}"`;
    });

    alert(`✅ Profile settings updated:\n\n${summary.join("\n")}`);
    setSubmitFlash("✅ All profile settings saved.");
    setTimeout(() => setSubmitFlash(""), 4000);
  };

  const handleGoToProfiles = () =>
    navigate(userid ? `/user-profiles/${userid}` : "/user-profiles");

  // ── Render inner tab ──────────────────────────────────────────────────
  const renderInner = () => {
    switch (innerTab) {
      case "feed":     return <FeedViewSection />;
      case "themes":   return <ThemeSection  profileData={profileData} updatedProfiles={updatedProfiles} onProfileChange={handleProfileChange} />;
      case "media":    return <MediaSection  profileData={profileData} updatedProfiles={updatedProfiles} onProfileChange={handleProfileChange} />;
      case "password": return <PasswordSection />;
      default:         return <FeedViewSection />;
    }
  };

  return (
    <div className="st-panel ps-root">

      {/* ── Panel header ─────────────────────────────────────────────── */}
      <h2 className="st-panel-title">Profile Settings</h2>
      <p  className="st-panel-subtitle">
        Configure your feed view, theme preferences, media display and account security.
      </p>

      {/* ── Inner tab strip ──────────────────────────────────────────── */}
      <div className="ps-inner-tabs" style={{gap: 10}}>
        {INNER_TABS.map(t => (
          <button
            key={t.key}
            className={`sd-submit-btn ${innerTab === t.key ? "ps-inner-tab--active" : ""}`}
            onClick={() => setInnerTab(t.key)}
            style={{gap: 10, marginLeft: 5, border: "1px solid gray" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Inner tab content ─────────────────────────────────────────── */}
      <div className="ps-inner-content">
        {renderInner()}
      </div>

      {/* ── Global submit bar (themes + media tabs only) ───────────────── */}
      {(innerTab === "themes" || innerTab === "media") && (
        <div className="ps-global-action-bar">
          {submitFlash && (
            <FlashBanner message={submitFlash} type="success" />
          )}
          <div className="ps-global-action-bar-btns" style={{marginTop: 20}}>
            <button
              type="button"
              className="st-save-btn"
              onClick={handleGoToProfiles}
              style={{background: "rgba(36,36,45,0.2)" }}
            >
              Goto Profiles
            </button>
            <button
              type="button"
              className="st-save-btn ps-action-btn"
              onClick={handleSubmitAll}
              disabled={Object.keys(updatedProfiles).length === 0}
              style={{
                opacity: Object.keys(updatedProfiles).length === 0 ? 0.40 : 1,
                cursor:  Object.keys(updatedProfiles).length === 0 ? "not-allowed" : "pointer",
                marginLeft: 20,
                background: "rgba(94, 94, 98, 0.2)",
                color: "white",
                marginTop: 30
              }}
            >
              Save All Profile Changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSettings;